import React, { useCallback, useEffect, useState } from 'react';
import { C, S } from '../../../utils/styles';
import Icon from '../../Icon';
import { useToast } from '../../Toast';
import {
  listBulkHistory,
  getBulkHistoryDetail,
  audienceLabel,
} from './bulkEmailApi';

function formatDateTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch (_) { return '—'; }
}

function statusPillStyle(status) {
  if (status === 'sent') {
    return { background: C.greenLight, color: C.green, border: `1px solid ${C.greenBorder}` };
  }
  if (status === 'failed') {
    return { background: C.redLight, color: C.red, border: `1px solid ${C.red}33` };
  }
  return { background: C.bg, color: C.textMid, border: `1px solid ${C.border}` };
}

export default function BulkEmailHistory({ refreshKey }) {
  const showToast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // full audit record
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listBulkHistory(200);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast(err.message || 'Failed to load history', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ id });
    try {
      const d = await getBulkHistoryDetail(id);
      setDetail(d);
    } catch (err) {
      showToast(err.message || 'Failed to load audit record', 'error');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div data-testid="bulk-email-history">
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={S.heading}>Send history ({items.length})</div>
          <button type="button" data-testid="history-refresh" onClick={load} style={{ ...S.buttonSecondary, padding: '6px 12px', fontSize: 12 }}>
            <Icon name="refresh-outline" style={{ fontSize: 12, marginRight: 4, verticalAlign: '-1px' }} />
            Refresh
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
            <thead>
              <tr>
                <th style={S.th}>Date Sent</th>
                <th style={S.th}>Subject</th>
                <th style={S.th}>Audience</th>
                <th style={S.th}>Recipients</th>
                <th style={S.th}>Sender</th>
                <th style={S.th}>Status</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td style={{ ...S.td, color: C.textLight, fontStyle: 'italic' }} colSpan={7}>Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td style={{ ...S.td, color: C.textLight, fontStyle: 'italic' }} colSpan={7}>
                  No bulk emails sent yet.
                </td></tr>
              )}
              {!loading && items.map((row) => (
                <tr key={row.id} data-testid={`history-row-${row.id}`}>
                  <td style={S.td}>{formatDateTime(row.sentAt)}</td>
                  <td style={S.td}>
                    <button
                      type="button"
                      data-testid={`history-open-${row.id}`}
                      onClick={() => openDetail(row.id)}
                      style={{
                        background: 'transparent', border: 'none', padding: 0,
                        color: C.accent, fontSize: 13, fontWeight: 600,
                        cursor: 'pointer', textAlign: 'left', fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      {row.subject}
                    </button>
                    {row.testOnly && (
                      <span style={{ ...pillBase, marginLeft: 6, background: C.purpleLight, color: C.purple }}>
                        TEST
                      </span>
                    )}
                  </td>
                  <td style={S.td}>{audienceLabel(row.audience)}</td>
                  <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace" }}>{row.recipientCount}</td>
                  <td style={S.td}>{row.sender?.name || '—'}</td>
                  <td style={S.td}>
                    <span style={{ ...pillBase, ...statusPillStyle(row.status) }}>
                      {row.status?.toUpperCase() || '—'}
                    </span>
                  </td>
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    <button
                      type="button"
                      data-testid={`history-view-${row.id}`}
                      onClick={() => openDetail(row.id)}
                      style={{ ...S.buttonSecondary, padding: '6px 12px', fontSize: 12 }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <DetailDrawer
          detail={detail}
          loading={detailLoading}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

const pillBase = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

function DetailDrawer({ detail, loading, onClose }) {
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.4)',
        backdropFilter: 'blur(2px)',
        zIndex: 200,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div
        data-testid="bulk-history-detail"
        style={{
          width: '100%',
          maxWidth: 720,
          height: '100%',
          background: '#fff',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.10)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Audit record
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {detail.subject || (loading ? 'Loading…' : '—')}
            </div>
          </div>
          <button type="button" data-testid="bulk-history-detail-close" onClick={onClose} style={{ ...S.buttonSecondary, padding: '6px 12px', fontSize: 12 }}>
            Close
          </button>
        </div>

        {loading && (
          <div style={{ padding: 24, color: C.textLight, fontStyle: 'italic' }}>Loading full record…</div>
        )}

        {!loading && (
          <div style={{ overflowY: 'auto', padding: 18 }}>
            {/* Meta */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 16 }}>
              <Meta label="Sent" value={detail.sentAt ? new Date(detail.sentAt).toLocaleString() : '—'} />
              <Meta label="Audience" value={audienceLabel(detail.audience)} />
              <Meta label="Recipients" value={detail.recipientCount} />
              <Meta label="Status" value={(detail.status || '—').toUpperCase()} />
              <Meta label="Sender" value={`${detail.sender?.name || '—'}${detail.sender?.email ? ` <${detail.sender.email}>` : ''}`} />
              <Meta label="Test only" value={detail.testOnly ? 'Yes' : 'No'} />
            </div>

            {detail.providerError && (
              <div style={{ padding: 10, borderRadius: 8, background: C.redLight, color: C.red, border: `1px solid ${C.red}33`, fontSize: 12, marginBottom: 12 }}>
                Provider error: {detail.providerError}
              </div>
            )}

            {/* Rendered HTML preview */}
            <div style={{ ...S.heading, marginTop: 8 }}>Rendered email</div>
            <div
              data-testid="bulk-history-detail-html"
              style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 0, overflow: 'hidden', marginBottom: 16, background: '#f8fafc' }}
            >
              {detail.renderedHtml ? (
                <iframe
                  title="Rendered email"
                  srcDoc={detail.renderedHtml}
                  style={{ width: '100%', height: 480, border: 'none', background: '#fff' }}
                />
              ) : (
                <div style={{ padding: 18, color: C.textLight, fontSize: 13, fontStyle: 'italic' }}>
                  No rendered HTML stored.
                </div>
              )}
            </div>

            {/* Recipient list */}
            <div style={S.heading}>Recipients ({(detail.recipients || []).length})</div>
            <div style={{ maxHeight: 240, overflowY: 'auto', border: `1px solid ${C.borderLight}`, borderRadius: 8 }}>
              {(detail.recipients || []).map((r, i) => (
                <div key={r.email + i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderBottom: `1px solid ${C.borderLight}`, fontSize: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: C.text, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name || r.email}</div>
                    <div style={{ color: C.textLight, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.email}{r.clientName ? ` · ${r.clientName}` : ''}</div>
                  </div>
                </div>
              ))}
              {(detail.recipients || []).length === 0 && (
                <div style={{ padding: 12, color: C.textLight, fontSize: 12, fontStyle: 'italic' }}>No recipients on this record.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 13, color: C.text, fontWeight: 500, marginTop: 2 }}>{value}</div>
    </div>
  );
}
